import React, { useEffect, useMemo, useState } from "react";
import { thesisApi, intelApi, scoutApi, offmarketApi } from "../../lib/api";
import { Card } from "../shared";
import { Compass, ExternalLink, Plus, Globe, Flame, Sprout, Users, Radar, Crosshair, Bookmark, Trash2 } from "lucide-react";
import { toast } from "sonner";

const METERS = [["demand", "Demand"], ["fragmentation", "Fragmentation"], ["competition", "Competition"], ["capital", "Capital access"]];
const MOMENTUM = { early: Sprout, rising: Compass, hot: Flame, late: Users };
const band = (n) => (n >= 80 ? "hot" : n >= 60 ? "warm" : n >= 40 ? "cool" : "cold");

const Consolidator = ({ p, tid, i }) => {
  const add = async () => {
    try {
      const res = await intelApi.promote({ audience: "investors", rows: [{ firm: p.name, name: p.name, type: p.type || "", thesis: p.note || "", why: p.note || "" }] });
      toast.success(res.added.length ? `${p.name} added to Investors` : `${p.name} is already in the CRM`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add them"); }
  };
  return (
    <div className="sig-player" data-testid={`thesis-player-${tid}-${i}`}>
      <b>{p.name}</b>
      {p.type ? <em>{p.type}</em> : null}
      <span>{p.note || ""}</span>
      <span className="sig-player-acts">
        <a href={`https://www.google.com/search?q=${encodeURIComponent(p.name)}`} target="_blank" rel="noreferrer" title={`Search the web for ${p.name}`}><Globe size={11} /></a>
        <button onClick={add} data-testid={`thesis-add-${tid}-${i}`} title={`Add ${p.name} to Investors`}><Plus size={11} />Investors</button>
      </span>
    </div>
  );
};

export const ThesisFinder = ({ dealKey, dealName, industry, geography }) => {
  const [ctx, setCtx] = useState({ industry: industry || "", geography: geography || "", notes: "", target: 20 });
  const [saved, setSaved] = useState([]);
  const [savedId, setSavedId] = useState("");
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openOnly, setOpenOnly] = useState(false);

  useEffect(() => { setCtx((c) => ({ ...c, industry: c.industry || industry || "", geography: c.geography || geography || "" })); }, [industry, geography]);
  useEffect(() => {
    thesisApi.last({ dealKey, dealName }).then((r) => {
      setRun(r && r.id ? r : null);
      if (r?.status === "running") setBusy(true);
    }).catch(() => setRun(null));
  }, [dealKey, dealName]);

  useEffect(() => {
    if (!busy || !run?.id) return;
    const t = setInterval(async () => {
      try {
        const r = await thesisApi.run(run.id);
        setRun(r);
        if (r.status !== "running") {
          setBusy(false);
          if (r.status === "error") toast.error(r.error || "The scan stopped early — partial theses are shown");
          else toast.success(`${r.theses.length} theses scored`);
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [busy, run?.id]);

  const loadSaved = () => thesisApi.searches().then((r) => setSaved(r.searches || [])).catch(() => setSaved([]));
  useEffect(() => { loadSaved(); }, []);

  const saveSearch = async () => {
    const name = window.prompt("Name this search", `${ctx.industry || "Market"} — ${ctx.geography || "US"}`.trim());
    if (name === null) return;
    try {
      await thesisApi.saveSearch({ name, runId: run?.id || "", context: { ...ctx, dealKey, dealName } });
      await loadSaved();
      toast.success(`Saved “${name}” — reload it any time from the dropdown`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the search"); }
  };

  const loadSearch = async (id) => {
    const hit = saved.find((x) => x.id === id);
    if (!hit) return;
    setCtx({ industry: hit.context.industry || "", geography: hit.context.geography || "",
      notes: hit.context.notes || "", target: hit.context.target || 20 });
    setSavedId(id);
    if (hit.runId) {
      try {
        const r = await thesisApi.run(hit.runId);
        setRun(r);
        if (r.status === "running") setBusy(true);
      } catch { toast.error("Those findings are no longer available — run the hunt again"); }
    }
    toast.success(`Loaded “${hit.name}”`);
  };

  const removeSearch = async (id) => {
    try { await thesisApi.deleteSearch(id); setSavedId(""); await loadSaved(); toast.success("Saved search removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove it"); }
  };

  const scan = async () => {
    setBusy(true);
    try {
      setRun(await thesisApi.scan({ ...ctx, dealKey, dealName }));
      toast.success("Hunting for theses — trending and uncontested…");
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); setBusy(false); }
  };

  const theses = useMemo(() => (run?.theses || []).filter((t) => !openOnly || t.openField), [run, openOnly]);

  // A thesis is only useful if it turns into a search: push it into Deal Scout (brokered flow)
  // and into Off-Market Targets (direct outreach flow).
  const toDealScout = async (t) => {
    try {
      await scoutApi.createSearch({
        name: `${t.thesis}${t.niche ? ` — ${t.niche}` : ""}`.slice(0, 90),
        sector: t.niche || ctx.industry || "",
        markets: [ctx.geography].filter(Boolean),
        sources: ["BizBuySell", "Axial", "Broker sites"],
      });
      toast.success(`“${t.thesis}” added to Deal Scout as a saved search`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add it to Deal Scout"); }
  };

  const toOffMarket = async (t) => {
    const tid = toast.loading("Setting the off-market criteria and searching the web…");
    try {
      await offmarketApi.saveCriteria({
        dealKey, niche: t.niche || t.thesis, homeBase: ctx.geography || "", radiusMiles: 100,
        notes: [t.thesis, t.detail, t.playbook].filter(Boolean).join(" · ").slice(0, 600),
      });
      const res = await offmarketApi.discover({ query: `${t.niche || t.thesis} ${ctx.industry || ""}`.trim(), location: ctx.geography || "", count: 25 });
      const rows = (res.entities || []).map((e) => ({ company: e.name || "", website: e.website || "", phone: e.phone || "",
        address: e.address || "", city: e.city || "", state: e.state || "", owner: e.owner || "", services: e.services || "",
        employees: e.employees ?? null, founded: e.founded ?? null, revenue: e.revenue ?? null, notes: e.note || t.thesis }))
        .filter((r) => r.company);
      if (rows.length) await offmarketApi.bulk({ items: rows, dealKey, dealName, autoScore: false });
      toast.success(`Off-market criteria set to “${t.niche || t.thesis}”${rows.length ? ` · ${rows.length} targets imported` : " · no targets found yet"}`, { id: tid });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not push it to Off-Market Targets", { id: tid }); }
  };

  return (
    <>
      <Card title="Thesis Finder" testid="thesis-input" right={(
        <button className="btn btn-primary" onClick={scan} disabled={busy} data-testid="thesis-run">
          <Compass size={13} style={{ marginRight: 6 }} />{busy ? "Hunting…" : "Find Thesis"}
        </button>
      )}>
        <div className="intel-grid">
          <div className="field"><label>Industry / starting point</label><input className="inp inp-text" value={ctx.industry} onChange={(e) => setCtx({ ...ctx, industry: e.target.value })} data-testid="thesis-industry" /></div>
          <div className="field"><label>Geography</label><input className="inp inp-text" value={ctx.geography} onChange={(e) => setCtx({ ...ctx, geography: e.target.value })} placeholder="United States" data-testid="thesis-geo" /></div>
          <div className="field"><label>Theses to find (15–30)</label>
            <input className="inp inp-text" type="number" min="15" max="30" value={ctx.target}
              onChange={(e) => setCtx({ ...ctx, target: Math.max(15, Math.min(30, +e.target.value || 20)) })} data-testid="thesis-target" /></div>
          <div className="field" style={{ gridColumn: "span 2" }}><label>Anything to steer the hunt</label>
            <input className="inp inp-text" value={ctx.notes} onChange={(e) => setCtx({ ...ctx, notes: e.target.value })}
              placeholder="e.g. recurring-revenue services, essential trades, nothing requiring licences" data-testid="thesis-notes" /></div>
        </div>
        <div className="sig-actions">
          <button className="btn" onClick={saveSearch} data-testid="thesis-save-search"><Bookmark size={13} style={{ marginRight: 5 }} />Save this search</button>
          {saved.length ? (
            <>
              <select className="inp" style={{ width: 220 }} value={savedId} onChange={(e) => e.target.value && loadSearch(e.target.value)} data-testid="thesis-saved-list">
                <option value="">Saved searches ({saved.length})</option>
                {saved.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
              <button className="btn btn-sm" disabled={!savedId} onClick={() => removeSearch(savedId)} title="Remove the selected search" data-testid="thesis-delete-search"><Trash2 size={12} /></button>
            </>
          ) : null}
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11.5 }}>
            <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} data-testid="thesis-openonly" />
            Only show open field (low competition, high fragmentation)
          </label>
          {busy && run ? (
            <span className="ir-fine" data-testid="thesis-progress">
              {run.phase === "scoring" ? "Scoring demand, fragmentation and competition…" : `Reading the web — ${run.anglesDone || 0}/${run.angles || 3} angles · ${(run.theses || []).length} theses`}
            </span>
          ) : null}
          {run && !busy ? <span className="ir-fine" data-testid="thesis-meta">Last hunt {String(run.ranAt || "").slice(0, 16).replace("T", " ")} · {(run.theses || []).length} theses</span> : null}
        </div>
      </Card>

      {run?.read ? <Card title="Where the best thesis is" style={{ marginTop: 12 }} testid="thesis-read"><div className="sig-read">{run.read}</div></Card> : null}

      <Card title={`Thesis${theses.length ? ` · ${theses.length}` : ""}`} style={{ marginTop: 12 }} testid="thesis-list">
        {!theses.length
          ? <div className="docs-empty" data-testid="thesis-empty">{run?.status === "error" ? run.error : busy ? "Reading the web — theses land here as each angle completes." : "Nothing hunted yet — set the industry above and hit “Find Thesis”."}</div>
          : theses.map((t) => {
            const Icon = MOMENTUM[(t.momentum || "").toLowerCase()] || Compass;
            return (
              <div className={`sig-card ${band(t.opportunity || 0)}`} key={t.id} data-testid={`thesis-${t.id}`}>
                <div className="sig-score" data-testid={`thesis-score-${t.id}`}><b>{t.opportunity || 0}</b><span>opportunity</span></div>
                <div className="sig-body">
                  <div className="sig-head">
                    <span className="sig-kind"><Icon size={11} /> {t.momentum || "trend"}</span>
                    <b>{t.thesis}</b>
                    {t.niche ? <em>{t.niche}</em> : null}
                    {t.openField ? <span className="th-open" data-testid={`thesis-open-${t.id}`}>open field</span> : null}
                    {t.multiples ? <em>{t.multiples}</em> : null}
                  </div>
                  <div className="sig-detail">{t.detail}</div>
                  <div className="th-meters">
                    {METERS.map(([k, label]) => (
                      <span key={k} className={`intel-sub${k === "competition" ? " inv" : ""}`}>
                        <i>{label}<u>{t[k] ?? 0}</u></i><b style={{ "--w": `${t[k] ?? 0}%` }} />
                      </span>
                    ))}
                  </div>
                  {t.whyUnderserved ? <div className="sig-now"><Sprout size={11} /> {t.whyUnderserved}</div> : null}
                  {t.fragmentationNote ? <div className="sig-detail">{t.fragmentationNote}</div> : null}
                  {t.verdict ? <div className="sig-action"><span>Verdict</span>{t.verdict}</div> : null}
                  {t.action ? <div className="sig-action"><span>First move</span>{t.action}</div> : null}
                  {t.playbook ? <div className="sig-action"><span>Playbook</span>{t.playbook}</div> : null}
                  <div className="th-acts">
                    <button className="btn btn-sm" onClick={() => toDealScout(t)} data-testid={`thesis-to-scout-${t.id}`}>
                      <Radar size={12} style={{ marginRight: 4 }} />Add to Deal Scout
                    </button>
                    <button className="btn btn-sm" onClick={() => toOffMarket(t)} data-testid={`thesis-to-offmarket-${t.id}`}>
                      <Crosshair size={12} style={{ marginRight: 4 }} />Add to Off-Market Targets
                    </button>
                  </div>
                  {(t.consolidators || []).length ? (
                    <div className="sig-players" data-testid={`thesis-players-${t.id}`}>
                      {t.consolidators.map((p, i) => <Consolidator key={i} p={p} tid={t.id} i={i} />)}
                    </div>
                  ) : null}
                  {(t.sources || []).length ? (
                    <div className="intel-sources">
                      {t.sources.map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer">{`source ${i + 1}`}<ExternalLink size={9} /></a>)}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
      </Card>
    </>
  );
};
